<script setup lang="ts">
import { ref } from "vue";
import { appContainer } from "../../../container";
import { LoadingService } from "../../../services/loadingService";
import { CompanyService } from "../../../services/manageCompanyService";
import { ToastService } from "../../../services/toastService";
import {
  CompanyInterface,
  CompanyType,
} from "../../../interface/CompanyInterface";
import { Roles } from "../../../interface/UserInterface";
import { ErrorResponse } from "../../../interface/ErrorInterface";
import { AxiosError } from "axios";
import ValidationErrorComponent from "../../../components/ValidationErrorComponent.vue";
import { ActiveRolesStore } from "../../../stores/ActiveRolesStore";
const activeRoleStore = ActiveRolesStore();
// Define and initialize the refs
const uploadImage = ref<string>("");
const logo = ref<File>();
const companyData = ref({
  name: "",
  representative_email: "",
  description: "",
  type: "",
});

const errors = ref<{
  name?: string;
  email?: string;
  logo?: string;
  type?: string;
  description?: string;
}>({});
const validationErrors = ref<ErrorResponse>();

const companyService = appContainer.resolve(CompanyService);
const loadingService = appContainer.resolve(LoadingService);
const toastService = appContainer.resolve(ToastService);

// Define the handleFileUpload function
const handleFileUpload = (e: Event) => {
  const target = e.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    const file = target.files[0];
    if (file.size > 2 * 1024 * 1024) {
      // 2 MB
      errors.value.logo = "File size should not exceed 2 MB.";
    } else {
      logo.value = file;
      uploadImage.value = URL.createObjectURL(file);
      errors.value.logo = "";
    }
  }
};

const removeImage = () => {
  uploadImage.value = "";
  logo.value = undefined;
};

// Function to add company and emit data to parent
const addCompany = async () => {
  if (activeRoleStore.activeRoles.includes(Roles.Admin)) {
    companyData.value.type = CompanyType.Company;
  }

  if (validateForm()) {
    const loader = await loadingService.show();
    companyService
      .addCompany(companyData.value as CompanyInterface, logo.value as File)
      .then(() => {
        toastService.success("Company Added Successfully");
        emit("companyAdded");
      })
      .catch((err) => {
        const error = err as AxiosError;
        const errorResponse = error.response?.data as ErrorResponse;
        validationErrors.value = errorResponse;
      })
      .finally(() => loader.hide());
  }
};

// Function to validate the form
const validateForm = () => {
  errors.value = {};
  let isValid = true;

  if (!companyData.value.name) {
    errors.value.name = "Name is required.";
    isValid = false;
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!companyData.value.representative_email) {
    errors.value.email = "Email is required.";
    isValid = false;
  } else if (!emailPattern.test(companyData.value.representative_email)) {
    errors.value.email = "Invalid email format.";
    isValid = false;
  }
  if (!companyData.value.description) {
    errors.value.description = "Introduction is required.";
    isValid = false;
  }
  if (!logo.value) {
    errors.value.logo = "Please upload a logo.";
    isValid = false;
  }
  if (!companyData.value.type) {
    errors.value.type = "Please select an organization type.";
    isValid = false;
  }

  return isValid;
};

const emit = defineEmits(["close", "companyAdded"]);

function visibleTo(roles: string[]) {
  return activeRoleStore.activeRoles?.some((item) => roles.includes(item));
}
</script>

<template>
  <div class="modal-backdrop">
    <div class="modal fade show" id="edit-modal">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <p class="modal-title fw-bold">Add Company</p>
            <button
              type="button"
              class="btn-close"
              @click="emit('close')"
            ></button>
          </div>
          <ValidationErrorComponent
            v-if="validationErrors"
            :validations="validationErrors"
          ></ValidationErrorComponent>
          <form class="w-100 overflow-y-auto" @submit.prevent="addCompany">
            <div class="modal-body">
              <div class="mb-3">
                <label for="companyName" class="form-label fw-bold">Name</label>
                <input
                  id="companyName"
                  type="text"
                  v-model="companyData.name"
                  placeholder="Name"
                  maxlength="80"
                  class="form-control"
                />
                <div v-if="errors.name" class="text-danger error mt-2">
                  {{ errors.name }}
                </div>
              </div>
              <div class="mb-3">
                <label for="representativeEmail" class="form-label fw-bold"
                  >Representative Email</label
                >
                <input
                  id="representativeEmail"
                  type="email"
                  maxlength="50"
                  v-model="companyData.representative_email"
                  placeholder="Email"
                  class="form-control"
                />
                <div v-if="errors.email" class="text-danger error mt-2">
                  {{ errors.email }}
                </div>
              </div>
              <div class="mb-3">
                <label for="introduction" class="form-label fw-bold"
                  >Introduction for Organization</label
                >
                <textarea
                  id="introduction"
                  v-model="companyData.description"
                  class="form-control"
                  rows="5"
                  data-cy=""
                  maxlength="1000"
                ></textarea>
                <div v-if="errors.description" class="text-danger error mt-2">
                  {{ errors.description }}
                </div>
              </div>
              <div class="mb-3" v-if="visibleTo([Roles.SuperAdmin])">
                <label for="organizationType" class="form-label fw-bold"
                  >Organization Type</label
                >
                <select
                  id="organizationType"
                  v-model="companyData.type"
                  class="form-select text-capitalize"
                  rows="5"
                  data-cy=""
                  maxlength="500"
                >
                  <option value="">Please Select Organization Type</option>
                  <option
                    v-for="(type, index) in CompanyType"
                    :key="index"
                    :value="type"
                  >
                    {{ type }}
                  </option>
                </select>
                <div v-if="errors.type" class="text-danger error mt-2">
                  {{ errors.type }}
                </div>
              </div>
              <div class="mb-3">
                <label for="companyLogo" class="form-label fw-bold">Logo</label>
                <div
                  class="d-flex w-100 align-items-start justify-content-between"
                >
                  <div
                    class="fileUpload text-center btn btn-primary btn-sm text-white position-relative"
                  >
                    <input
                      id="companyLogo"
                      type="file"
                      @change="handleFileUpload($event)"
                      accept="image/png, image/jpg, image/jpeg"
                      class="upload position-absolute top-0 end-0 bottom-0 start-0"
                    />
                    <span>Upload Image <i class="bi bi-upload ms-2"></i></span>
                  </div>
                  <div
                    class="img-uploaded rounded position-relative ms-2 mb-2"
                    v-if="uploadImage"
                  >
                    <button
                      @click="removeImage"
                      class="btn-remove-img rounded-pill text-white d-flex align-items-center justify-content-center p-0 border-0 position-absolute"
                    >
                      <i class="bi bi-x"></i>
                    </button>
                    <img
                      :src="uploadImage"
                      alt="Uploaded Image"
                      class="w-100 h-100 objectfit-contain border rounded"
                    />
                  </div>
                </div>
                <div v-if="errors.logo" class="text-danger error mt-2">
                  {{ errors.logo }}
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="submit"
                class="btn btn-primary btn-sm mx-auto text-white"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fileUpload {
  width: 175px;
}
.fileUpload input.upload {
  opacity: 0;
}
input[type="file"] {
  position: fixed;
  right: 100%;
  bottom: 100%;
}
.img-uploaded {
  width: 100px;
  height: 100px;
}
.btn-remove-img {
  width: 18px;
  height: 18px;
  right: -5px;
  top: -5px;
  background-color: #dc3545;
}
.form-control {
  padding: 12px 16px;
}
.text-danger {
  color: #dc3545;
}
</style>
